import { connect } from 'react-redux'
import React from 'react'
import Loader from 'components/D3Loader'
import { setNavBarProps } from 'store/navbar'
import { getBaseSiteIdFromStudy, getUserRoleByStudy, getUserScopeByStudy, getUserRoleIdByStudy, isIncentivesEnabled } from 'utils/misc'
import { USER_ROLE_ID_MAP, USER_ROLES_MAP, USER_SCOPES, USER_ROLES_CAN_ACCESS_HISTORY } from 'utils/constants'
import { PERMISSION_FOR_VIEWING_PARTICIPANT_VISITS } from 'utils/userPermissionConstants'
import { checkIsEmailless, isCaregiverV1Study } from 'utils/studyConfigUtils'
import CreateParticipantPage from '../components/CreateParticipantPage'
import { actions } from '../modules/CreateParticipant'
import { actions as ptpActions, downloadBulkData } from '../../ParticipantsPage/modules/Participants'
import { actions as siteActions } from '../../../../Sites/routes/SitesPage/modules/Sites'
import {
  downloadParticipantConsent,
  fetchConsentEvents,
} from '../../../../Consent/routes/ConsentEditor/modules/Consent'
import {
  downloadCompletedParticipantConsent,
  uploadPtpConsentAttachments,
} from '../../../../Consents/routes/ConsentsDashboard/modules/Consents'
import { isIrtControlledVisitRestricted, isVersionUatOrLive } from 'utils/studyConfigUtils'
import { USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS } from 'utils/constants'
import { isEmpty } from 'utils/misc'
import { hasMultipleArms } from 'utils/visitScheduleArms'

const mapStateToProps = (state, ownProps) => {
  const { studyVersionID } = ownProps?.params || {}
  const {
    consents,
    user,
    participantReducer,
    loading,
    sending,
    tableFilters,
    sites,
    location,
    study,
    studyLock,
    userReducer,
    visitSchedules,
    participants: participantsState,
  } = state
  const { currentStudy, orphanedParticipants, studyVersionList } = study
  const { consent_required } = currentStudy
  const { participant, files } = participantReducer
  const isCreate = state.location.pathname.includes('create')
  const studyID = parseInt(ownProps.params.studyID, 10)
  const userScope = getUserScopeByStudy(user, currentStudy)
  const currentUserRole = getUserRoleByStudy(user, studyID)
  const hasMarkInstrumentComplete = !!currentStudy.config?.mark_instrument_complete
  const hasTreasureChest = !!currentStudy.config?.super_gems
  const { sendingState, sentState } = sending
  const sendDisabled = sendingState || sentState
  const isDiary = !!currentStudy.config?.ediary
  const canViewVisitSchedule = PERMISSION_FOR_VIEWING_PARTICIPANT_VISITS.some(scope => userScope.includes(scope))
  const canWrite = userScope.includes('wp')
  const isSupportRole = currentUserRole.includes(USER_ROLES_MAP.supportAgent)
  const hasVisitSupport = !!currentStudy.config?.visit_support
  const isCollectConfirmedTime = !!currentStudy.config?.visit_support?.enable_visit_time
  const hasStartVisitId = !!currentStudy.config?.start_visit_id
  const hasVisitStartDatetime = !!currentStudy.config?.visit_start_datetime
  const roleId = getUserRoleIdByStudy(user, studyID)
  const hasIncentives = isIncentivesEnabled(currentStudy.config)
  const sitesInfo = () => {
    if (!userReducer?.userSites) return {}
    const newSitesInfo = { ...userReducer.userSites }
    Object.keys(newSitesInfo).forEach(key => {
      if (newSitesInfo[key].level === 1) delete newSitesInfo[key]
    })
    return newSitesInfo
  }
  const participantDataFields = currentStudy.config?.participant_data_fields
  const isEmailless = checkIsEmailless(currentStudy)
  const noTracksFeature = currentStudy.config?.tracks?.disabled === true
  const hasTracks = Array.isArray(participantsState?.cohortList) && participantsState.cohortList.length > 0
  const tracksEnabled = !noTracksFeature && hasTracks
  const hasSubjectIdValidation = currentStudy.config?.study_validations?.participant_subject_id_uniqueness?.enabled
  const subjectIdSiteBased = currentStudy.config?.study_validations?.participant_subject_id_uniqueness?.site_based
  const requireMissedVisitReason = currentStudy?.config?.require_missed_visit_reason

  const isVisitCycleSupported = currentStudy?.config?.visit_support?.version > 1
  const isArmsSupported = currentStudy?.config?.visit_support?.arms_enabled

  const participantSiteId = ownProps.ptpSiteID || (isCreate ? null : parseInt(ownProps.params.siteID, 10))

  const sitesList = sites.sitesList
  const ptpSite = sitesList?.find(site => String(site?.[0]?.value) === String(participantSiteId))
  const ptpSiteConfig = ptpSite?.find(item => item.key === 'siteName')?.config

  const manualConsentEnabled = ptpSiteConfig?.manual_consent || false

  const isEnroller = roleId === USER_ROLE_ID_MAP.enroller

  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const isReadOnlyState = isSupportRole || (hasStudyPromotion && isVersionUatOrLive(studyVersionList, studyVersionID))

  const canViewHistory = USER_ROLES_CAN_ACCESS_HISTORY.includes(currentUserRole)

  const studyConfig = currentStudy?.config
  const isIrtControlledVisit = (visitId, participantVisitMetadata, studyScheduleVisitId) =>
    isIrtControlledVisitRestricted({
      config: studyConfig,
      studyVersionList,
      studyVersionID,
      roleId,
      visitId,
      participantVisitMetadata,
      studyScheduleVisitId,
      studyVisitSchedule: visitSchedules?.studyVisitSchedule,
    })

  const hideDinamically = {
    announcements: !isEmpty(currentStudy.config?.communication),
    visits: !!currentStudy.config?.visit_support,
    tracks: tracksEnabled,
    payments: isIncentivesEnabled(currentStudy.config),
    accountSettings: canWrite || isSupportRole,
  }

  return {
    ...participantReducer,
    canAddPtp: userScope.includes('wxp'),
    canMove: userScope.includes('mp'),
    canResendInviteOrResetPw: userScope.includes('rpe'),
    canSeeOrphans: [
      USER_ROLES_MAP.root,
      USER_ROLES_MAP.studyCoordinator,
      USER_ROLES_MAP.investigator,
      USER_ROLES_MAP.studyAdmin,
    ].includes(currentUserRole),
    announcementTimelineLoading: loading.announcementTimeline,
    canDownloadConsent: userScope.includes('xdc'),
    canUpdateConsent: userScope.includes('xc'),
    canUploadConsentAttachment: userScope.includes(USER_SCOPES.xcu.code),
    canViewAnnouncements: userScope.includes('xp') || isSupportRole,
    canViewFiles: userScope.includes('xp') || isSupportRole,
    canViewVisitSchedule,
    canWrite,
    checkedSites: sites.checkedSites,
    componentLoading: loading,
    consentRequired: consent_required,
    diaryTimelineLoading: loading.diaryTimeline,
    files,
    filterProps: tableFilters,
    hasIncentives,
    hasMarkInstrumentComplete,
    hasOrphanedParticipants: orphanedParticipants,
    hasRewards: ['cycles', 'reward_goal', 'reward_duration'].some(key => key in study && study[key] !== null),
    hasStartVisitId,
    hasSubjectIdValidation,
    subjectIdSiteBased,
    hasVisitStartDatetime,
    hasVisitSupport,
    hideDinamically,
    inLeafSite: participant.in_leaf_site,
    instrumentTimelineLoading: loading.instrumentTimeline,
    instrumentSubmissionLoading: loading.instrumentSubmission,
    isCreate,
    isDiary,
    isEmailless,
    isCollectConfirmedTime,
    isInParticipantsTable: location.pathname.endsWith('/participants'),
    isSiteCoord:
      roleId === USER_ROLE_ID_MAP.siteCoordinator ||
      roleId === USER_ROLE_ID_MAP.clinicalCoordinator ||
      roleId === USER_ROLE_ID_MAP.siteCoordinatorUAT ||
      roleId === USER_ROLE_ID_MAP.clinicalCoordinatorUAT ||
      roleId === USER_ROLE_ID_MAP.siteCoordinatorDEV ||
      roleId === USER_ROLE_ID_MAP.clinicalCoordinatorDEV,
    isSponsor:
      roleId === USER_ROLE_ID_MAP.sponsor ||
      roleId === USER_ROLE_ID_MAP.sponsorUAT ||
      roleId === USER_ROLE_ID_MAP.sponsorDEV ||
      roleId === USER_ROLE_ID_MAP.sponsorUserMonitor,
    leafSites: sites.leafSites,
    loading: loading.general,
    noPII: userScope.includes('rp-no-pii') || isSupportRole,
    noTracksFeature,
    tracksEnabled,
    ptpAnnouncementsLoading: loading.participantAnnouncements,
    ptpHistoryLoading: loading.participantsHistory,
    ptpConsentAttachmentLoading: loading.consentAttachment,
    ptpId: ownProps.ptpId ? ownProps.ptpId : participant.id,
    ptpInstrumentsLoading: loading.participantInstruments,
    ptpMoveLoading: loading.moveParticipants,
    requestSending: sendingState,
    requestSent: sentState,
    sendDisabled,
    siteSelectionList: sites.siteSelectionList,
    sitesLanguages: sites.sitesLanguages,
    study: currentStudy,
    studyID,
    studyLock,
    tempPwdLoading: loading.tempPassword,
    /**
     * the logic used in the following lines is to get the site ID values depending on
     * whether the participant page is in the preview drawer or in its own route
     */
    baseSiteID: ownProps.siteID || getBaseSiteIdFromStudy(state.study),
    ptpSiteID: participantSiteId,
    hasTreasureChest,
    participantDataFields,
    sitesInfo: sitesInfo(),
    userId: user.id,
    roleId,
    visitStartDatetime: participant?.visit_start_datetime,
    unCompleteBlockedConsents: consents?.unCompletedBlockedConsents || {},
    requireMissedVisitReason,
    manualConsentEnabled,
    isVisitCycleSupported,
    studyVisitSchedule: visitSchedules?.studyVisitSchedule,
    isArmsSupported,
    isArmsDisplayed: hasMultipleArms(visitSchedules?.studyVisitSchedule),
    isReadOnlyState,
    isEnroller,
    isCaregiverV1: isCaregiverV1Study(currentStudy?.config),
    studyVersionID,
    hasStudyPromotion,
    canViewHistory,
    isIrtControlledVisit,
    isClinOrSiteCoordinator: USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS.includes(roleId),
    // Extract subject IDs from participants for INCR auto-increment
    existingSubjectIds: isCreate && participantsState?.ptpMap
      ? Object.values(participantsState.ptpMap)
        .map(row => {
          const subjectIdItem = row.find(item => item?.key === 'subjectId')
          return subjectIdItem?.value
        })
        .filter(id => id && id !== '---' && id !== null && id !== undefined) // Filter out empty/placeholder values
      : [],
    // Extract pre-screen IDs from participants for INCR auto-increment
    existingPreScreenIds: isCreate && participantsState?.ptpMap
      ? Object.values(participantsState.ptpMap)
        .map(row => {
          const preScreenIdItem = row.find(item => item?.key === 'preScreenId')
          return preScreenIdItem?.value
        })
        .filter(id => id && id !== '---' && id !== null && id !== undefined) // Filter out empty/placeholder values
      : [],
  }
}

const mapDispatchToProps = {
  ...actions,
  ...siteActions,
  ...ptpActions,
  setNavBarProps,
  downloadBulkData,
  downloadParticipantConsent,
  downloadCompletedParticipantConsent,
  uploadPtpConsentAttachments,
  fetchConsentEvents,
  fetchPtp: (studyId, siteId, ptpId) => actions.fetchParticipant(studyId, siteId, ptpId),
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(props => {
  if (props.loading) {
    return Loader(props)
  }
  return <CreateParticipantPage {...props} />
})
